import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Button } from '@/shared/ui/Button';

import { useExecutionFindings, useExecutions, useRequestExecution } from '../api/rules';

/* Run the rules Auto Suggest just bound, in one execution, on this dataset only.
 *
 * **The existing execution path, scoped by binding.** It sends the bound bindings' ids and this
 * dataset's id, so the run measures exactly what was just bound — not every rule on the table, and
 * not the same rule on every table its selector reaches.
 *
 * **One run per press.** The button is busy while the request is in flight and stays down while
 * that run is queued or running; it comes back as "Run again" once the run has finished. The server
 * refuses a second in-flight run of the same plan with a 409 as well, so a lost response that is
 * retried cannot start a second scan of a customer's table.
 *
 * Status and results are read from the same polled executions and findings the Executions screen
 * uses, so they move while the run does and agree with that screen when the reader opens it.
 */
export function RunRecommendedRules({
  bindingIds,
  datasetId,
}: {
  readonly bindingIds: readonly string[];
  readonly datasetId: string;
}): ReactElement | null {
  const { t } = useTranslation();
  const run = useRequestExecution();
  const executions = useExecutions();
  const findings = useExecutionFindings();

  if (bindingIds.length === 0) return null;

  const started = run.data;
  const live =
    started === undefined
      ? undefined
      : (executions.data?.items.find((each) => each.id === started.id) ?? started);
  /* `null` until a task has succeeded, which is a different fact from zero violations — a queued
     run must never read as a clean one. */
  const violations =
    started === undefined
      ? null
      : (findings.data?.items.find((each) => each.execution_id === started.id)
          ?.violations ?? null);
  const inFlight =
    live !== undefined && (live.state === 'pending' || live.state === 'running');

  return (
    <div className="lq-autosg__run">
      <Button
        tone="primary"
        busy={run.isPending}
        disabled={inFlight}
        title={t('dataset.rules.runAllMeaning')}
        onClick={() => {
          run.mutate({ bindingIds: [...bindingIds], datasetIds: [datasetId] });
        }}
      >
        {live === undefined || inFlight
          ? t('dataset.rules.auto.run.go', { count: bindingIds.length })
          : t('dataset.rules.auto.run.again')}
      </Button>
      {run.isError ? (
        <span className="lq-autosg__error" role="alert">
          {t('dataset.rules.runAllRefused', { reason: run.error.message })}
        </span>
      ) : null}
      {live === undefined ? null : (
        <span className="lq-autosg__status" role="status">
          <strong>{t(`executions.state.${live.state}.label`)}</strong>
          {' · '}
          {t('dataset.rules.auto.run.progress', {
            done:
              live.progress.succeeded + live.progress.failed + live.progress.cancelled,
            total: live.progress.total,
          })}
          {violations === null
            ? null
            : ` · ${t('dataset.rules.auto.run.violations', { count: violations })}`}{' '}
          <Link className="lq-autosg__link" to={`/executions/${live.id}`}>
            {t('dataset.rules.auto.run.open')}
          </Link>
        </span>
      )}
    </div>
  );
}
